import {FC, useEffect, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {UserIcon} from '@/components/common/icons/UserIcon'
import {ModalWrapper} from '@/components/common/modal/ModalWrapper'
import {useHeaderContent} from '@/content/header'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {getCurrentSearchParams} from '@/utils/browser/location'
import {useNavigation} from '@/utils/hooks/useNavigation'
import {getWithoutLocale} from '@/utils/i18n/restrictedPaths'
import {AuthActionType, isAuthActionType} from '@/utils/user/constants'

import {AuthModalLazy} from '../../user/auth/AuthModalLazy'

type LoginButtonProps = {
  isFullWidth?: boolean
}

export const LoginButton: FC<LoginButtonProps> = ({isFullWidth = false}) => {
  const {t} = useTranslation('page')

  const {push} = useNavigation()
  const {loginButtonText} = useHeaderContent()
  const [activeAuthActionType, setActiveAuthActionType] = useState<AuthActionType | null>(null)

  // `?user=sign-in` opens the modal. Read from `window.location` on mount rather than through
  // `useSearchParams()`: this button sits in the header of every page, so subscribing there would
  // opt every page out of static rendering. The link is always followed as a full page load.
  useEffect(() => {
    const user = getCurrentSearchParams().get('user')
    // Validate against the enum: an unknown value used to open the modal with no content in it.
    if (isAuthActionType(user)) {
      setActiveAuthActionType(user)
    }
  }, [])

  const handleClickOnSignIn = () => setActiveAuthActionType(AuthActionType.signIn)

  return (
    <>
      <Button
        variant={ButtonVariant.darkGray}
        size={isFullWidth ? ButtonSize.large : ButtonSize.medium}
        data-test="signInButton"
        onClick={() => handleClickOnSignIn()}
        type="button"
        fullWidth={isFullWidth}
        icon={<UserIcon color={theme.palette.text.white} />}
        iconPosition="left"
        hasBorder
        isTextHiddenOnMobile
        ariaLabel={t(loginButtonText)}
      >
        {t(loginButtonText)}
      </Button>
      <ModalWrapper
        isVisible={Boolean(activeAuthActionType)}
        props={
          activeAuthActionType
            ? {
                authActionType: activeAuthActionType,
                setActiveAuthActionType,
                closeModal: () => {
                  // Drop `?user=` so reopening the page does not reopen the modal. Click
                  // handler, so the live URL is authoritative.
                  const params = getCurrentSearchParams()
                  params.delete('user')
                  const query = params.toString()
                  push(`${getWithoutLocale(window.location.pathname)}${query ? `?${query}` : ''}`)
                  setActiveAuthActionType(null)
                },
              }
            : null
        }
      >
        {(props) => <AuthModalLazy {...props} />}
      </ModalWrapper>
    </>
  )
}
